import React, { useState, useEffect, useMemo } from 'react';
import { hierarchicalCodes, industryCodes, getAllChildCodes } from '../../data/industryCodes';
import type { IndustryCode } from '../../data/industryCodes';

interface IndustryCodeSelectProps {
  onSelectIndustryCodes: (codes: string[]) => void;
  initialSelectedCodes?: string[];
}

export default function IndustryCodeSelect({ 
  onSelectIndustryCodes, 
  initialSelectedCodes = [] 
}: IndustryCodeSelectProps) {
  const [selectedCodes, setSelectedCodes] = useState<string[]>(initialSelectedCodes);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'tree' | 'search'>('tree');
  
  // Notify parent component when selected codes change
  useEffect(() => {
    onSelectIndustryCodes(selectedCodes);
  }, [selectedCodes, onSelectIndustryCodes]);
  
  // Toggle expanded state for a category
  const toggleCategory = (code: string) => {
    setExpandedCategories(prev => 
      prev.includes(code) 
        ? prev.filter(c => c !== code) 
        : [...prev, code]
    );
  };
  
  // Handle selecting/deselecting an industry code
  const handleSelectCode = (code: string, includeChildren: boolean = false) => {
    const codeExists = selectedCodes.includes(code);
    
    if (codeExists) {
      // If deselecting, also deselect all child codes
      const childCodes = getAllChildCodes(code);
      setSelectedCodes(prev => 
        prev.filter(c => c !== code && !childCodes.includes(c))
      );
    } else {
      // If selecting, optionally select all child codes
      if (includeChildren) {
        const childCodes = getAllChildCodes(code);
        const newCodes = [code, ...childCodes].filter(c => !selectedCodes.includes(c));
        setSelectedCodes(prev => [...prev, ...newCodes]);
      } else {
        setSelectedCodes(prev => [...prev, code]);
      }
    }
  };
  
  // Handle clearing all selected codes
  const handleClearAll = () => {
    setSelectedCodes([]);
  };
  
  // Filter industry codes based on search term
  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return industryCodes.all.filter(code => 
      code.name.toLowerCase().includes(lowerSearchTerm) || 
      code.code.toLowerCase().includes(lowerSearchTerm)
    ).slice(0, 100); // Limit results to 100 for performance
  }, [searchTerm]);
  
  // Recursive function to render the hierarchical tree
  const renderCategoryTree = (codes: IndustryCode[], depth = 0) => {
    return codes.map(category => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedCategories.includes(category.code);
      const isSelected = selectedCodes.includes(category.code);
      
      return (
        <div key={category.code} style={{ marginLeft: `${depth * 20}px` }}>
          <div className="flex items-center py-1">
            {hasChildren && (
              <button
                type="button"
                onClick={() => toggleCategory(category.code)}
                className="mr-2 text-gray-500 focus:outline-none"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}
            <div className="flex items-center flex-1">
              <input
                type="checkbox"
                id={`category-${category.code}`}
                checked={isSelected}
                onChange={() => handleSelectCode(category.code)}
                className="mr-2"
              />
              <label
                htmlFor={`category-${category.code}`}
                className="text-sm cursor-pointer text-gray-800 hover:text-blue-600 flex-1"
              >
                <span className="font-mono text-xs text-gray-500 mr-2">{category.code}</span>
                {category.name}
              </label>
              {hasChildren && (
                <button
                  type="button"
                  onClick={() => handleSelectCode(category.code, true)}
                  className="text-xs text-blue-600 hover:text-blue-800 ml-2"
                  title="Velg alle underkategorier"
                >
                  + alle
                </button>
              )}
            </div>
          </div>
          
          {hasChildren && isExpanded && (
            <div className="mt-1 border-l-2 border-gray-200 pl-2">
              {renderCategoryTree(category.children || [], depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-lg font-semibold mb-3">Næringsfilter</h3>
      
      {/* Search and view mode buttons */}
      <div className="flex mb-4">
        <div className="relative flex-1 mr-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value.trim()) {
                setViewMode('search');
              } else {
                setViewMode('tree');
              }
            }}
            placeholder="Søk etter næringskode eller navn..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-white pr-10"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setViewMode('tree');
              }}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setViewMode('tree')}
          className={`px-3 py-1 rounded text-sm ${
            viewMode === 'tree'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Tre
        </button>
        <button
          type="button"
          onClick={() => setViewMode('search')}
          className={`px-3 py-1 rounded text-sm ml-2 ${
            viewMode === 'search'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Liste
        </button>
      </div>
      
      {/* Industry codes display area */}
      <div className="max-h-80 overflow-y-auto border rounded p-3 bg-gray-50">
        {viewMode === 'tree' && !searchTerm && (
          <div className="space-y-1">
            {renderCategoryTree(hierarchicalCodes)}
          </div>
        )}
        
        {(viewMode === 'search' || searchTerm) && (
          <>
            {filteredCodes.length > 0 ? (
              <div className="space-y-1">
                {filteredCodes.map(code => (
                  <div key={code.code} className="flex items-center py-1">
                    <input
                      type="checkbox"
                      id={`search-${code.code}`}
                      checked={selectedCodes.includes(code.code)}
                      onChange={() => handleSelectCode(code.code)}
                      className="mr-2"
                    />
                    <label
                      htmlFor={`search-${code.code}`}
                      className="text-sm cursor-pointer text-gray-800 hover:text-blue-600 flex-1"
                    >
                      <span className="font-mono text-xs text-gray-500 mr-2">{code.code}</span>
                      {code.name}
                      <span className="text-xs text-gray-500 ml-2">({`Nivå ${code.level}`})</span>
                    </label>
                  </div>
                ))}
              </div>
            ) : searchTerm ? (
              <div className="text-center py-4 text-gray-500">
                Ingen næringskoder funnet. Prøv et annet søkeord.
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                Velg visning eller søk etter næringskoder.
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Selected codes summary */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Valgte næringskoder ({selectedCodes.length})
          </span>
          {selectedCodes.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Fjern alle
            </button>
          )}
        </div>
        
        {selectedCodes.length > 0 ? (
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded bg-blue-50">
            {selectedCodes.map(code => {
              const industry = industryCodes.all.find(i => i.code === code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs"
                >
                  <span className="font-medium mr-1">{code}</span>
                  {industry?.shortName || industry?.name || ''}
                  <button
                    type="button"
                    onClick={() => handleSelectCode(code)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic">
            Ingen næringskoder valgt. Velg fra listen ovenfor.
          </div>
        )}
      </div>
    </div>
  );
}